import { Checkbox, TextInput } from '@mantine/core';
import { useField, useForm } from '@mantine/form';
import {
  AssessmentRecord,
  GrowthPlanSession,
  GrowthPlanSessionExtended,
} from '@scaleup/models';
import { IconCheck, IconUser, IconUsers } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import {
  useGetUserQuery,
  useGetUsersQuery,
  useSendAssessmentInvitesMutation,
  useUpdateSessionsGuestsMutation,
} from '../../api';
import { useToggle } from '../../hooks';
import { Button, Conditional, Modal } from '../../ui';
import { validateEmail } from '../../utils';

const ROW_COLOR_TO_STYLE = {
  blue: 'bg-[#d3ecf3]',
  grey: 'bg-[#eaecf0]',
};

const InviteRowWithCheckbox = ({
  email,
  onChange,
  color,
  disabled,
  checked,
}: {
  email: string;
  disabled: boolean;
  color: 'blue' | 'grey';
  onChange: (val: boolean, email: string) => void;
  checked: boolean;
}) => (
  <div className="flex justify-between items-center py-1">
    <div className="flex gap-2 items-center">
      <div
        className={`${ROW_COLOR_TO_STYLE[color]!} rounded-full w-[3.5rem] h-[3.5rem] flex justify-center items-center`}
      >
        <IconUser color={color === 'blue' ? '#4fb3cf' : '#98a1b4'} />
      </div>
      <p className="text-text-secondary">{email}</p>
    </div>

    <div>
      <Checkbox
        defaultChecked={checked}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.checked, email);
        }}
      />
    </div>
  </div>
);

const InviteRow = ({
  email,
  color,
}: {
  email: string;
  color: 'blue' | 'grey';
}) => (
  <div className="flex items-center py-1">
    <div className="flex gap-2 items-center">
      <div
        className={`${ROW_COLOR_TO_STYLE[color]!} rounded-full w-[3.5rem] h-[3.5rem] flex justify-center items-center`}
      >
        <IconUser color={color === 'blue' ? '#4fb3cf' : '#98a1b4'} />
      </div>
      <p className="text-text-secondary">{email}</p>
    </div>
  </div>
);

type Invite = { email: string; isNew: boolean; name?: string; userId?: string };

interface BaseInviteModalProps {
  onClose: () => void;
  sendInvites: (
    x: { email: string; isNew: boolean; name?: string; userId?: string }[]
  ) => Promise<void>;
  sendInvitesLoading: boolean;
  isUserAlreadyInvited: (e: string) => boolean;
  viewOnly?: boolean;
}

interface BaseInviteModalShowProps {
  maxInvites?: number;
  sendInviteModalTitle: string;
  invitesSentModalTitle: string;
  sendInviteTitle: string | JSX.Element;
  invitesSentTitle: string | JSX.Element;
  nonTeamMemberExistingInvites?: Invite[];
}

const useBaseInviteModal = ({
  sendInvites,
  sendInvitesLoading,
  isUserAlreadyInvited,
  onClose,
  viewOnly = false,
}: BaseInviteModalProps) => {
  const [show, toggle] = useToggle(false);
  const [invitesSent, setInvitesSent] = useState(false);
  const [props, setProps] = useState<BaseInviteModalShowProps>();

  const [invites, setInvites] = useState<Invite[]>([]);

  const emailField = useField({
    initialValue: '',
    validate: (value) =>
      validateEmail(value) ? null : 'Please provide a valid email',
  });
  const { data: users, isLoading: usersLoading } = useGetUsersQuery();
  const { data: user, isLoading: userLoading } = useGetUserQuery();

  const form = useForm({
    initialValues: {
      invites: [],
    },
  });

  const showInviteModal = (p: BaseInviteModalShowProps) => {
    setProps(p);
    toggle();
  };

  const handleSendInvites = async () => {
    const { userId, firstName, lastName, userEmail } = user!;
    await sendInvites([
      {
        email: userEmail,
        userId,
        isNew: false,
        name: `${firstName} ${lastName}`,
      },
      ...invites,
    ]);
    setInvitesSent(true);
  };

  const addInvite = () => {
    form.insertListItem('invites', { email: emailField.getValue() });
    emailField.reset();
  };

  const closeModal = () => {
    form.reset();
    setInvites([]);
    setInvitesSent(false);
    emailField.reset();
    onClose();
    toggle();
  };

  const isEmailFieldValid = () => {
    return validateEmail(emailField.getValue());
  };

  const inviteOnChange = (
    val: boolean,
    email: string,
    isNew: boolean,
    userId?: string,
    name?: string
  ) => {
    if (val) {
      setInvites([...invites, { email, isNew }]);
    } else {
      invites.splice(
        invites.findIndex((it) => it.email === email),
        1
      );
      setInvites([...invites]);
    }
  };

  const shouldDisableRow = (email: string) => {
    if (
      props!.maxInvites &&
      invites.length === props!.maxInvites &&
      invites.findIndex((it) => it.email === email) === -1
    ) {
      return true;
    }

    return false;
  };

  const sendInviteUI = () => {
    return (
      <>
        <p className="text-text-secondary text-md -mt-4 pb-2">
          {props!.sendInviteTitle}
        </p>

        <Conditional condition={!viewOnly}>
          <div className="py-2 flex gap-4 justify-between items-center w-full">
            <div className="w-4/6">
              <TextInput
                {...emailField.getInputProps()}
                classNames={{ input: 'rounded-[1rem]' }}
                type="email"
                placeholder="user@joinscaleup.com"
              />
            </div>

            <div className="w-2/6">
              <Button
                slim
                text="Add"
                variant={isEmailFieldValid() ? 'primary' : 'disabled'}
                onClick={addInvite}
              />
            </div>
          </div>
        </Conditional>

        {/* new invites */}
        {(props!.nonTeamMemberExistingInvites || [])
          .filter(
            (it) => users!.findIndex((u) => u.userEmail === it.email) === -1
          )
          .map(({ email }) => (
            <InviteRowWithCheckbox
              key={email}
              checked={true}
              disabled={shouldDisableRow(email)}
              color="grey"
              email={email}
              onChange={(val, email) => inviteOnChange(val, email, true)}
            />
          ))}
        {form.getValues().invites.map(({ email }) => (
          <InviteRowWithCheckbox
            key={email}
            checked={false}
            disabled={shouldDisableRow(email)}
            color="grey"
            email={email}
            onChange={(val, email) => inviteOnChange(val, email, true)}
          />
        ))}

        {/* existing users */}
        {(users || []).length > 1 && (
          <>
            <p className="text-text-primary font-bold">Active Team Members</p>
            {(users || []).map(({ userEmail, userId, firstName, lastName }) => (
              <InviteRowWithCheckbox
                key={userId}
                checked={
                  isUserAlreadyInvited(userEmail) || userId === user!.userId
                }
                disabled={
                  shouldDisableRow(userEmail) || userId === user!.userId
                }
                color="blue"
                email={userEmail}
                onChange={(val, email) =>
                  inviteOnChange(
                    val,
                    email,
                    false,
                    userId,
                    `${firstName} ${lastName}`
                  )
                }
              />
            ))}{' '}
          </>
        )}

        <Conditional
          condition={
            (invites.length > 0 ||
              (props!.nonTeamMemberExistingInvites || []).length > 0) &&
            !viewOnly
          }
        >
          <div className="w-full flex justify-end pt-2">
            <Button
              text="Send Invite"
              loading={sendInvitesLoading}
              variant="primary"
              onClick={handleSendInvites}
            />
          </div>
        </Conditional>
      </>
    );
  };

  const invitesSentUI = () => {
    return (
      <>
        <p className="text-text-secondary text-md -mt-4 pb-2">
          {props!.invitesSentTitle}
        </p>

        {/* new invites */}
        {invites
          .filter(({ isNew }) => isNew)
          .map(({ email }) => (
            <InviteRow key={email} color="grey" email={email} />
          ))}

        {/* existing invites */}
        {invites
          .filter(({ isNew }) => !isNew)
          .map(({ email }) => (
            <InviteRow key={email} color="blue" email={email} />
          ))}

        <div className="w-full flex justify-end pt-2">
          <Button text="Done" variant="primary" onClick={closeModal} />
        </div>
      </>
    );
  };

  const renderInviteModal = () => {
    if (!show) {
      return null;
    }

    return (
      <Modal
        fullScreen={false}
        title={
          invitesSent
            ? props!.invitesSentModalTitle
            : props!.sendInviteModalTitle
        }
        icon={
          invitesSent ? (
            <IconCheck color="#297d53" />
          ) : (
            <IconUsers color="#4298b0" />
          )
        }
        iconSize={48}
        iconBackground={true}
        iconRounded
        showModal={show}
        handleCloseModal={closeModal}
      >
        <div className="w-full">
          {invitesSent ? invitesSentUI() : sendInviteUI()}
        </div>
      </Modal>
    );
  };

  return {
    show: showInviteModal,
    render: renderInviteModal,
    close: closeModal,
  };
};

export const useAssessmentInviteUserModal = () => {
  const [assessmentRecord, setAssessmentRecord] = useState<AssessmentRecord>();

  const [sendAssessmentInvites, { isLoading: sendAssessmentInvitesLoading }] =
    useSendAssessmentInvitesMutation();

  const showInviteModal = (ar: AssessmentRecord) => {
    setAssessmentRecord(ar);
    const currentUserCount = Object.keys(ar.users || {}).length;
    const inviteLimit = 3 - currentUserCount;

    show({
      sendInviteModalTitle: 'Invite User',
      invitesSentModalTitle: 'Invites Sent',
      invitesSentTitle: `An invitation has been sent to the following users to complete the
      ${ar.assessmentName || 'assessment'}.`,
      sendInviteTitle: `You can invite up to ${inviteLimit} additional user(s) to complete the ${ar.assessmentName || 'assessment'}.`,
      maxInvites: inviteLimit,
    });
  };

  const handleSendInvites = async (invites: Invite[]) => {
    await sendAssessmentInvites({
      recordId: assessmentRecord!.assessmentRecordId,
      invites: invites.map(({ email }) => email),
    });
  };

  const isUserAlreadyInvited = (email: string) => {
    const users = assessmentRecord?.users || {};
    const existingUserInvites = Object.keys(users).map(
      (it) => users[it]?.userName
    );
    return existingUserInvites.includes(email);
  };

  const { show, render } = useBaseInviteModal({
    sendInvites: handleSendInvites,
    sendInvitesLoading: sendAssessmentInvitesLoading,
    isUserAlreadyInvited,
    onClose: () => setAssessmentRecord(undefined),
  });

  return { showInviteModal, renderInviteModal: render };
};

export const useGrowthPlanInviteUserModal = (viewOnly = false) => {
  const [session, setSession] = useState<
    GrowthPlanSession & GrowthPlanSessionExtended
  >();

  const [sendInvites, { isLoading: invitesLoading }] =
    useUpdateSessionsGuestsMutation();

  const isUserAlreadyInvited = (email: string) => {
    const guests = session!.guests || [];
    const existingUserInvites = guests.map((it) => it.email);
    return existingUserInvites.includes(email);
  };

  const handleSendInvites = async (invites: Invite[]) => {
    await sendInvites({
      sessionNumber: session!.sessionId,
      guests: invites.map(({ email, name, userId }) => ({
        email,
        name,
        userId,
      })),
    });
  };

  const { show, render } = useBaseInviteModal({
    sendInvites: handleSendInvites,
    sendInvitesLoading: invitesLoading,
    isUserAlreadyInvited,
    onClose: () => setSession(undefined),
    viewOnly,
  });

  const showInviteModal = (
    s: GrowthPlanSession & GrowthPlanSessionExtended
  ) => {
    setSession(s);
    show({
      nonTeamMemberExistingInvites: (s.guests || [])
        .filter((it) => it.userId === undefined)
        .map((it) => ({
          email: it.email,
          isNew: true,
        })),
      sendInviteModalTitle: viewOnly ? 'Meeting Guests' : 'Invite Guests',
      invitesSentModalTitle: 'Invitation Sent',
      invitesSentTitle: (
        <>
          An invitation has been sent to the following users to join
          <strong>{`Session ${s.sessionNumber}: ${s.title}`}</strong>
        </>
      ),
      sendInviteTitle: viewOnly ? (
        <strong>{`Session ${s.sessionNumber}: ${s.title}`}</strong>
      ) : (
        <>
          Invite team members to join{' '}
          <strong>{`Session ${s.sessionNumber}: ${s.title}`}</strong>
        </>
      ),
      maxInvites: undefined,
    });
  };

  return { showInviteModal, renderInviteModal: render };
};
